import { baseURL } from "database/onlineDb/baseUrl";
import { ENTITLEMENT_API_VERSION, validateEntitlementProjection } from "./entitlementProjection";
import { isOffline, NETWORK_OFFLINE, OFFLINE_MESSAGE } from "./netGuard";

const ERROR_CONTRACT = {
  AUTHENTICATION_REQUIRED: [401, "Authentication is required.", false],
  FORBIDDEN: [403, "You are not permitted to access entitlement data.", false],
  INVALID_REQUEST: [400, "The request is invalid.", false],
  RATE_LIMITED: [429, "Too many entitlement requests. Try again later.", true],
  ENTITLEMENT_UNAVAILABLE: [500, "Entitlement request is temporarily unavailable. Try again later.", true],
  INTERNAL_ERROR: [500, "Entitlement request could not be completed. Try again later.", true],
  IDEMPOTENCY_KEY_REUSED: [409, "The idempotency key is already bound to a different request.", false],
  RESOURCE_NOT_FOUND: [404, "The requested entitlement resource was not found.", false],
  CONFIRMATION_TOKEN_INVALID: [409, "The quote confirmation token is invalid.", false],
  QUOTE_EXPIRED: [409, "The quote has expired. Request a new estimate.", false],
  QUOTE_SNAPSHOT_STALE: [409, "The estimate is stale. Request a new estimate.", false],
  UNSUPPORTED_WORKFLOW: [422, "The workflow is not supported.", false],
  INVALID_UNITS: [422, "The unit list is invalid.", false],
  INSUFFICIENT_CAPACITY: [422, "The entire batch was rejected because its maximum capacity could not be reserved. No upload began.", false],
  PERMISSION_CONFLICT: [409, "The requested upload permission conflicts with current reservation state.", false],
  EFFECT_CONFLICT: [409, "The reservation already has a different durable outcome.", false],
  RESERVATION_OUTCOME_UNKNOWN: [503, "The reservation outcome is unknown. Refresh entitlement and reconcile the reservation.", true],
  RECONCILIATION_FAILED: [503, "Reservation reconciliation failed. Your entitlement remains protected; try again later.", true],
};

const DEFAULT_TIMEOUT_MS = 15000;
const ID = /^[0-9a-f]{32}$/;
const TOKEN = /^[\x21-\x7e]{1,128}$/;
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const exact = (value, keys) => record(value) && Object.keys(value).length === keys.length && keys.every((key) => own(value, key));
const positiveInteger = (value) => Number.isSafeInteger(value) && value > 0;
const timestamp = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value) &&
  !Number.isNaN(Date.parse(value)) && new Date(value).toISOString() === value;
const money = (value) => {
  if (typeof value !== "string" || !/^(0|[1-9][0-9]*)\.[0-9]{2}$/.test(value)) return false;
  const whole = value.split(".")[0];
  return whole.length < 10 || whole.length === 10 && value <= "9999999999.99";
};
const count = (value) => Number.isSafeInteger(value) && value >= 0 && value <= 4294967295;
const cents = (value) => BigInt(value.replace(".", ""));
const nullableTimestamp = (value) => value === null || timestamp(value);
const workflowPair = (workflow, billingUnitType) => ({
  "regular-bulk": "bulk-image",
  "patient-upload": "patient-image",
})[workflow] === billingUnitType;
const GUID = /^(?:[A-Za-z0-9]{32}|c[A-Za-z0-9_-]{19,31})$/;
const MODELS = new Set(["Normal-Dr-NonDr", "Dr-Other", "Normal-Dr-NonDr-Pipeline-2"]);

export class EntitlementReservationError extends Error {
  constructor({ code = "ENTITLEMENT_UNAVAILABLE", message, retryable = true, status = 0, requestId = null, idempotencyKey, reservationId } = {}) {
    const fallback = ERROR_CONTRACT.ENTITLEMENT_UNAVAILABLE;
    super(message || fallback[1]);
    this.name = "EntitlementReservationError";
    this.code = code;
    this.retryable = retryable;
    this.status = status;
    this.requestId = requestId;
    if (idempotencyKey) this.idempotencyKey = idempotencyKey;
    if (reservationId) this.reservationId = reservationId;
  }
}

const unavailable = (status = 0) => new EntitlementReservationError({ status });
const invalidRequest = () => new EntitlementReservationError({ code: "INVALID_REQUEST", message: ERROR_CONTRACT.INVALID_REQUEST[1], retryable: false, status: 400 });
const reconciliationFailed = (status = 503) => {
  const [expectedStatus, message, retryable] = ERROR_CONTRACT.RECONCILIATION_FAILED;
  return new EntitlementReservationError({ code: "RECONCILIATION_FAILED", message, retryable, status: status || expectedStatus });
};
const unknownOutcome = (idempotencyKey, reservationId) => {
  const [status, message, retryable] = ERROR_CONTRACT.RESERVATION_OUTCOME_UNKNOWN;
  return new EntitlementReservationError({ code: "RESERVATION_OUTCOME_UNKNOWN", message, retryable, status, idempotencyKey, reservationId });
};

const validateCancellation = (value) => {
  const terminalReplay = exact(value, ["state", "replay"]) && ["CANCELLED", "RELEASED"].includes(value.state) && value.replay === true;
  const retained = exact(value, ["state", "retained", "reason"]) && value.state === "RETAINED_RECONCILIATION" && value.retained === true && value.reason === "ACTIVE_WORK";
  const released = exact(value, ["state", "replay", "releasedUnits", "projectionChanged"]) &&
    ["CANCELLED", "RETAINED_RECONCILIATION"].includes(value.state) && typeof value.replay === "boolean" &&
    Array.isArray(value.releasedUnits) && value.releasedUnits.length > 0 && value.releasedUnits.every(positiveInteger) &&
    new Set(value.releasedUnits).size === value.releasedUnits.length && typeof value.projectionChanged === "boolean";
  if (!terminalReplay && !retained && !released) throw unavailable(200);
  return value;
};
const validateError = (payload, status) => {
  const code = payload && payload.error && payload.error.code;
  const contract = ERROR_CONTRACT[code];
  if (!exact(payload, ["apiVersion", "error"]) || payload.apiVersion !== ENTITLEMENT_API_VERSION ||
    !exact(payload.error, ["code", "message", "requestId", "retryable"]) || !contract ||
    contract[0] !== status || payload.error.message !== contract[1] || payload.error.retryable !== contract[2] ||
    typeof payload.error.requestId !== "string" || !/^[\x20-\x7e]{1,128}$/.test(payload.error.requestId)) {
    throw unavailable(status);
  }
  throw new EntitlementReservationError({ ...payload.error, status });
};

const validUnit = (unit, withSequence) => exact(unit, withSequence ? ["unitSequence", "canonicalUnitIdentity"] : ["canonicalUnitIdentity"]) &&
  (!withSequence || positiveInteger(unit.unitSequence)) && typeof unit.canonicalUnitIdentity === "string" && unit.canonicalUnitIdentity.length > 0;
const validCanonicalIdentity = (identity, workflow) => {
  let value;
  try { value = JSON.parse(identity); } catch (error) { return false; }
  if (JSON.stringify(value) !== identity) return false;
  const storedWorkflow = workflow === "regular-bulk" ? "BULK" : workflow === "patient-upload" ? "PATIENT_UPLOAD" : null;
  return storedWorkflow && exact(value, ["workflow", "billingUnitType", "guid", "laterality", "s3Bucket", "s3Key", "members"]) &&
    value.workflow === storedWorkflow && value.billingUnitType === "IMAGE" && GUID.test(value.guid) && ["left", "right"].includes(value.laterality) &&
    /^[\x21-\x7e]{1,63}$/.test(value.s3Bucket) && /^[^\x00-\x1f\x7f]{1,1024}$/.test(value.s3Key) && Array.isArray(value.members) && value.members.length > 0 &&
    value.members.every((member, index) => exact(member, ["memberSetSequence", "workSequence", "model"]) && member.memberSetSequence === 1 && member.workSequence === index + 1 && MODELS.has(member.model)) &&
    new Set(value.members.map((member) => member.model)).size === value.members.length;
};
const sameUnits = (left, right) => Array.isArray(left) && Array.isArray(right) && left.length === right.length &&
  left.every((unit, index) => unit.canonicalUnitIdentity === right[index].canonicalUnitIdentity);

const validEstimate = (value) => exact(value, ["maximumUnitAmount", "estimatedMaximumBatchLiability", "proBonoUnitsRequired", "paidUnitsRequired", "paidCapacityRequired"]) &&
  money(value.maximumUnitAmount) && money(value.estimatedMaximumBatchLiability) && count(value.proBonoUnitsRequired) && count(value.paidUnitsRequired) && money(value.paidCapacityRequired);
const validCapacity = (value) => exact(value, ["availableProBonoUnits", "paidCapacityAvailable"]) && count(value.availableProBonoUnits) && money(value.paidCapacityAvailable);
const validPricing = (value) => exact(value, ["unitPrice"]) && money(value.unitPrice);
const validEntitlementSnapshot = (value) => exact(value, ["paid", "proBono"]) &&
  exact(value.paid, ["total", "reserved", "available"]) && [value.paid.total, value.paid.reserved, value.paid.available].every(money) &&
  exact(value.proBono, ["totalUnits", "reservedUnits", "availableUnits"]) && [value.proBono.totalUnits, value.proBono.reservedUnits, value.proBono.availableUnits].every(count) &&
  cents(value.paid.total) === cents(value.paid.reserved) + cents(value.paid.available) && value.proBono.totalUnits === value.proBono.reservedUnits + value.proBono.availableUnits;
const coherentQuote = (value) => {
  const maximum = cents(value.estimate.maximumUnitAmount);
  const maximumPrice = cents(value.pricingSnapshot.unitPrice);
  return maximum === maximumPrice && cents(value.estimate.estimatedMaximumBatchLiability) === BigInt(value.itemCount) * maximum &&
    value.estimate.proBonoUnitsRequired + value.estimate.paidUnitsRequired === value.itemCount &&
    cents(value.estimate.paidCapacityRequired) === BigInt(value.estimate.paidUnitsRequired) * maximum &&
    value.capacitySnapshot.availableProBonoUnits === value.entitlementSnapshot.proBono.availableUnits &&
    value.capacitySnapshot.paidCapacityAvailable === value.entitlementSnapshot.paid.available;
};

const validateQuote = (value) => {
  const valid = exact(value, ["quoteId", "workflow", "billingUnitType", "issuedAt", "expiresAt", "units", "itemCount", "pricingSnapshot", "capacitySnapshot", "estimate", "entitlementSnapshot", "confirmationToken"]) &&
    ID.test(value.quoteId) && timestamp(value.issuedAt) && timestamp(value.expiresAt) && Array.isArray(value.units) &&
    workflowPair(value.workflow, value.billingUnitType) && value.units.length === value.itemCount && value.itemCount <= 150 &&
    value.units.every((unit, index) => validUnit(unit, true) && unit.unitSequence === index + 1) && TOKEN.test(value.confirmationToken) &&
    value.units.every((unit) => validCanonicalIdentity(unit.canonicalUnitIdentity, value.workflow)) &&
    new Set(value.units.map((unit) => unit.canonicalUnitIdentity)).size === value.units.length && validPricing(value.pricingSnapshot, value.workflow) &&
    validCapacity(value.capacitySnapshot) && validEstimate(value.estimate) && validEntitlementSnapshot(value.entitlementSnapshot) && coherentQuote(value);
  if (!valid) throw unavailable();
  return value;
};

const validateReservation = (value) => {
  const valid = exact(value, ["reservationId", "quoteId", "state", "workflow", "billingUnitType", "createdAt", "updatedAt", "quote", "reserved", "effects", "reconciliation", "projection"]) &&
    ID.test(value.reservationId) && ID.test(value.quoteId) && timestamp(value.createdAt) && timestamp(value.updatedAt) &&
    ["reserved", "processing", "settled", "released", "cancelled"].includes(value.state) &&
    workflowPair(value.workflow, value.billingUnitType) && exact(value.quote, ["issuedAt", "expiresAt", "units", "pricingSnapshot", "capacitySnapshot", "estimate"]) &&
    timestamp(value.quote.issuedAt) && timestamp(value.quote.expiresAt) && Array.isArray(value.quote.units) && value.quote.units.length > 0 && value.quote.units.length <= 150 &&
    value.quote.units.every((unit, index) => validUnit(unit, true) && unit.unitSequence === index + 1) && validPricing(value.quote.pricingSnapshot, value.workflow) &&
    validCapacity(value.quote.capacitySnapshot) && validEstimate(value.quote.estimate) &&
    exact(value.reserved, ["paidAmount", "proBonoUnits"]) && money(value.reserved.paidAmount) && count(value.reserved.proBonoUnits) &&
    validateEffects(value.effects) && validateReconciliation(value.reconciliation);
  if (valid) {
    try { validateEntitlementProjection(value.projection); } catch (error) { throw reconciliationFailed(); }
  }
  if (!valid) throw unavailable();
  return value;
};

function validateEffects(value) {
  const effectState = (state) => ["not-required", "pending", "partially-applied", "applied", "unknown", "failed"].includes(state);
  return exact(value, ["paid", "proBono"]) &&
    exact(value.paid, ["reservedAmount", "remainingAmount", "capturedAmount", "releasedAmount", "captureState", "releaseState"]) &&
    [value.paid.reservedAmount, value.paid.remainingAmount, value.paid.capturedAmount, value.paid.releasedAmount].every(money) &&
    effectState(value.paid.captureState) && effectState(value.paid.releaseState) &&
    exact(value.proBono, ["reservedUnits", "remainingUnits", "capturedUnits", "releasedUnits", "captureState", "releaseState"]) &&
    [value.proBono.reservedUnits, value.proBono.remainingUnits, value.proBono.capturedUnits, value.proBono.releasedUnits].every(count) &&
    effectState(value.proBono.captureState) && effectState(value.proBono.releaseState);
}

function validateReconciliation(value) {
  return exact(value, ["state", "attemptCount", "lastAttemptAt", "nextAttemptAt"]) &&
    ["not-required", "pending", "complete", "failed"].includes(value.state) && count(value.attemptCount) && nullableTimestamp(value.lastAttemptAt) && nullableTimestamp(value.nextAttemptAt);
}

const validatePermission = (value) => {
  const valid = exact(value, ["reservationId", "unitSequence", "memberSetSequence", "permissionId", "permissionExpiresAt", "permissionRevokedAt", "objectWriteNonce", "guid", "laterality", "s3Bucket", "s3Key"]) &&
    ID.test(value.reservationId) && ID.test(value.permissionId) && ID.test(value.objectWriteNonce) && positiveInteger(value.unitSequence) &&
    positiveInteger(value.memberSetSequence) && timestamp(value.permissionExpiresAt) && value.permissionRevokedAt === null &&
    typeof value.guid === "string" && value.guid.length > 0 && ["left", "right"].includes(value.laterality) &&
    typeof value.s3Bucket === "string" && /^[\x21-\x7e]{1,63}$/.test(value.s3Bucket) && typeof value.s3Key === "string" && /^[^\x00-\x1f\x7f]{1,1024}$/.test(value.s3Key);
  if (!valid) throw unavailable();
  return value;
};

const validatePermissionState = (value) => {
  const valid = exact(value, ["reservationId", "batchState", "uploadEligible", "permissions"]) && ID.test(value.reservationId) &&
    ["ADMITTED", "IN_FLIGHT", "SETTLED", "RELEASED", "CANCELLED"].includes(value.batchState) &&
    typeof value.uploadEligible === "boolean" && Array.isArray(value.permissions) && value.permissions.length > 0 &&
    value.permissions.every((permission) => exact(permission, ["unitSequence", "memberSetSequence", "memberSetState", "permissionId", "permissionExpiresAt", "permissionRevokedAt"]) &&
      positiveInteger(permission.unitSequence) && positiveInteger(permission.memberSetSequence) && typeof permission.memberSetState === "string" &&
      (permission.permissionId === null || ID.test(permission.permissionId)) && (permission.permissionExpiresAt === null || timestamp(permission.permissionExpiresAt)) &&
      ["RESERVED", "AUTHORIZED", "UPLOAD_STARTED", "UPLOADED", "METADATA_ATTACHED", "ENQUEUE_INTENDED", "ENQUEUED", "RETAINED_RECONCILIATION", "REVOKED"].includes(permission.memberSetState) &&
      (permission.permissionRevokedAt === null || timestamp(permission.permissionRevokedAt)));
  const keys = valid ? value.permissions.map((item) => `${item.unitSequence}:${item.memberSetSequence}`) : [];
  const eligible = valid && (!value.uploadEligible || ["ADMITTED", "IN_FLIGHT"].includes(value.batchState) && value.permissions.every((item) => item.memberSetState === "AUTHORIZED" && item.permissionId && item.permissionExpiresAt && item.permissionRevokedAt === null));
  if (!valid || !eligible || new Set(keys).size !== keys.length) throw reconciliationFailed();
  return value;
};

const validateRequest = (request, keys) => {
  if (!exact(request, keys)) throw invalidRequest();
};

export const createEntitlementReservationClient = ({ token, baseUrl = baseURL, fetchImpl, requestTimeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
  if (typeof token !== "string" || token.length === 0) throw new EntitlementReservationError({ code: "AUTHENTICATION_REQUIRED", message: ERROR_CONTRACT.AUTHENTICATION_REQUIRED[1], retryable: false, status: 401 });
  const request = fetchImpl || (typeof window !== "undefined" && window.fetch ? window.fetch.bind(window) : null);
  if (!request) throw unavailable();
  const timeoutMs = Number.isFinite(requestTimeoutMs) && requestTimeoutMs > 0 ? requestTimeoutMs : DEFAULT_TIMEOUT_MS;

  const offline = () => new EntitlementReservationError({
    code: NETWORK_OFFLINE,
    message: OFFLINE_MESSAGE,
    retryable: true,
    status: 0,
  });

  const call = async ({ path, method, body, expectedStatuses, validate, idempotencyKey, unknownReservationId, mutation = false, recoveryRead = false }) => {
    if (isOffline()) throw offline();
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    let timer;
    let timedOut = false;
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        timedOut = true;
        if (controller) controller.abort();
        reject(mutation ? unknownOutcome(idempotencyKey, unknownReservationId) : recoveryRead ? reconciliationFailed() : unavailable());
      }, timeoutMs);
    });
    const headers = { Accept: "application/json", Authorization: `Bearer ${token}`, ...(body ? { "Content-Type": "application/json" } : {}), ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) };
    try {
      const operation = (async () => {
        const response = await request(`${baseUrl}${path}`, { method, cache: "no-store", ...(controller ? { signal: controller.signal } : {}), headers, ...(body ? { body: JSON.stringify(body) } : {}) });
        const payload = await response.json();
        if (!response.ok) return validateError(payload, response.status);
        if (!expectedStatuses.includes(response.status) || !exact(payload, ["apiVersion", "data"]) || payload.apiVersion !== ENTITLEMENT_API_VERSION) throw unavailable(response.status);
        return { status: response.status, data: validate(payload.data) };
      })();
      return await Promise.race([operation, timeout]);
    } catch (error) {
      if (error instanceof EntitlementReservationError && (error.requestId || error.code === NETWORK_OFFLINE || error.code === "RESERVATION_OUTCOME_UNKNOWN" || !mutation && !recoveryRead || recoveryRead && error.code === "RECONCILIATION_FAILED")) throw error;
      if (mutation) throw unknownOutcome(idempotencyKey, unknownReservationId);
      if (recoveryRead) throw reconciliationFailed();
      throw unavailable();
    } finally {
      clearTimeout(timer);
    }
  };

  return {
    async createQuote(quoteRequest) {
      validateRequest(quoteRequest, ["workflow", "billingUnitType", "units"]);
      if (!workflowPair(quoteRequest.workflow, quoteRequest.billingUnitType) || !Array.isArray(quoteRequest.units) || quoteRequest.units.length < 1 || quoteRequest.units.length > 150 ||
        quoteRequest.units.some((unit) => !validUnit(unit, false) || !validCanonicalIdentity(unit.canonicalUnitIdentity, quoteRequest.workflow)) ||
        new Set(quoteRequest.units.map((unit) => unit.canonicalUnitIdentity)).size !== quoteRequest.units.length) throw invalidRequest();
      const result = (await call({ path: "/api/v1/entitlement/quotes", method: "POST", body: quoteRequest, expectedStatuses: [201], validate: validateQuote })).data;
      if (result.workflow !== quoteRequest.workflow || result.billingUnitType !== quoteRequest.billingUnitType || !sameUnits(result.units, quoteRequest.units)) throw unavailable(201);
      return result;
    },
    async confirmReservation({ request: confirmationRequest, idempotencyKey } = {}) {
      validateRequest(confirmationRequest, ["quoteId", "workflow", "billingUnitType", "units", "confirmationToken"]);
      if (typeof idempotencyKey !== "string" || !TOKEN.test(idempotencyKey) || idempotencyKey.trim() !== idempotencyKey) throw invalidRequest();
      const result = await call({ path: "/api/v1/entitlement/reservations", method: "POST", body: confirmationRequest, expectedStatuses: [200, 201], validate: validateReservation, idempotencyKey, mutation: true });
      if (result.data.quoteId !== confirmationRequest.quoteId || result.data.workflow !== confirmationRequest.workflow ||
        result.data.billingUnitType !== confirmationRequest.billingUnitType || !sameUnits(result.data.quote.units, confirmationRequest.units)) throw unknownOutcome(idempotencyKey);
      return { status: result.status, replayed: result.status === 200, data: result.data };
    },
    async getReservation({ reservationId } = {}) {
      if (!ID.test(reservationId || "")) throw invalidRequest();
      const data = (await call({ path: `/api/v1/entitlement/reservations/${reservationId}`, method: "GET", expectedStatuses: [200], validate: validateReservation, recoveryRead: true })).data;
      if (data.reservationId !== reservationId) throw reconciliationFailed();
      return data;
    },
    async issuePermission({ reservationId, request: permissionRequest } = {}) {
      if (!ID.test(reservationId || "")) throw invalidRequest();
      validateRequest(permissionRequest, ["unitSequence", "memberSetSequence", "guid", "laterality", "s3Bucket", "s3Key"]);
      const result = await call({ path: `/api/v1/entitlement/reservations/${reservationId}/permissions`, method: "POST", body: permissionRequest, expectedStatuses: [200, 201], validate: validatePermission, mutation: true });
      if (result.data.reservationId !== reservationId || ["unitSequence", "memberSetSequence", "guid", "laterality", "s3Bucket", "s3Key"].some((key) => result.data[key] !== permissionRequest[key])) throw unknownOutcome();
      return { status: result.status, replayed: result.status === 200, data: result.data };
    },
    async getPermissions({ reservationId, expectedMemberSets } = {}) {
      if (!ID.test(reservationId || "")) throw invalidRequest();
      if (!Array.isArray(expectedMemberSets) || expectedMemberSets.length < 1 || expectedMemberSets.some((member) =>
        !exact(member, ["unitSequence", "memberSetSequence"]) || !positiveInteger(member.unitSequence) || !positiveInteger(member.memberSetSequence))) throw invalidRequest();
      const data = (await call({ path: `/api/v1/entitlement/reservations/${reservationId}/permissions`, method: "GET", expectedStatuses: [200], validate: validatePermissionState, recoveryRead: true })).data;
      if (data.reservationId !== reservationId) throw reconciliationFailed();
      const expectedKeys = expectedMemberSets.map((member) => `${member.unitSequence}:${member.memberSetSequence}`);
      const actualKeys = data.permissions.map((member) => `${member.unitSequence}:${member.memberSetSequence}`);
      if (new Set(expectedKeys).size !== expectedKeys.length || expectedKeys.length !== actualKeys.length || expectedKeys.some((key, index) => key !== actualKeys[index])) throw reconciliationFailed();
      return data;
    },
    async cancelReservation({ reservationId } = {}) {
      if (!ID.test(reservationId || "")) throw invalidRequest();
      return (await call({
        path: `/api/v1/entitlement/reservations/${reservationId}/cancel`,
        method: "POST",
        expectedStatuses: [200],
        validate: validateCancellation,
        mutation: true,
        unknownReservationId: reservationId,
      })).data;
    },
  };
};
